// This code is brought over from aops3's latex.js and made a bit more abstract.

const _ = require("lodash");
const PS = require("./ParserState");
const {escapeRegex} = require("../../../../shared/StringUtil");

// Returns true if the character at the given position of the text is preceded
// by an odd number of the given escape character.
const isOddEscaped = function(escapeChar, text, pos) {
	if (!escapeChar) {
		return false;
	}
	let isEscaped = false;
	pos -= 1;
	while (pos >= 0 && text[pos] === escapeChar) {
		isEscaped = !isEscaped;
		pos -= 1;
	}
	return isEscaped;
};

// Pass the delimiter row, "begin" or "end", the full string, and the position
// where the delimiter was found.
// Returns true if the given position is a valid occurrence of the delimiter,
// i.e. not escaped and does not have bad characters coming after. Assumes
// the delimiter appears at the given position, i.e. this is not checked.
const isValidDelim = function(delimRow, type, text, pos) {
	if (isOddEscaped(delimRow.escape, text, pos)) {
		return false;
	}
	const posAfter = pos + delimRow[type].length;
	if (delimRow.notAtEnd && delimRow.notAtEnd.test(text[posAfter])) {
		return false;
	}
	return true;
};

// Pass an array of fixed strings that should be searched for. (No patterns.)
// Returns a string that can be given to the RegExp constructor, not an
// actual regular expression.
const delimListAsPattern = function(delimList) {
	delimList = _.map(delimList, escapeRegex);
	return "(" + delimList.join("|") + ")";
};

// Pass a delimited string and the associated row in a delimTable. Returns an
// object with type, text, begin, body, end keys. See splitByDelimSet for
// what these mean. You can also pass a falsy value for delimRow to treat
// the text as undelimited.
const makeDelimBlock = function(text, delimRow) {
	if (delimRow && delimRow.end) {
		return {
			type: delimRow.type,
			text: text,
			begin: delimRow.begin,
			body: text.slice(
				delimRow.begin.length,
				text.length - delimRow.end.length
			),
			end: delimRow.end,
		};
	} else if (delimRow) {
		return {
			type: delimRow.type,
			text: delimRow.begin,
			begin: delimRow.begin,
			body: "",
			end: "",
		};
	} else {
		return {
			type: "text",
			text: text,
			begin: "",
			body: text,
			end: "",
		};
	}
};

// ps is a parser state.
// delimTable should be an array of objects with keys begin, end, type. end
// can be omitted if the delimiter is isolated.
// This only finds the first delimited block and returns an object with keys:
// - block: Object with type, text, begin, body, end keys. Concatenating the
//   text values of each object gets the original string. Ordinary, undelimited
//   text has type "text" and empty begin, body entries. The text value is
//   always the concatenation of the begin, body, end values.
// - start: The integer string index coming right before the found block.
// - end: The integer string index coming right after the found block.
// If no delimiter is found or no delimTable is provided, returns null.
// The parser state will be at the same position and have recorded errors.
const findNextDelim = function(ps, delimTable) {
	if (!delimTable) {
		return null;
	}
	ps = PS.convert(ps);

	// In these variable names, bd = beginDelim and ed = endDelim.

	// Regex for finding a beginning delimiter.
	// HACK: Cache the beginning regex pattern on the delim table, in case the
	// same one gets passed again. Improves performance.
	delimTable.__bdRegex =
		delimTable.__bdRegex ||
		new RegExp(delimListAsPattern(_.map(delimTable, "begin")));
	const bdRegex = delimTable.__bdRegex;

	// Executing the match on only the fragment of the text content. The found
	// positions will need to be moved up by textStart.
	const bdMatch = bdRegex.exec(ps.text.slice(ps.pos));
	// If no beginning delimiters, we're done.
	if (!bdMatch) {
		return null;
	}
	const bdPos = ps.pos + bdMatch.index;
	const bd = bdMatch[0];
	const bdLength = bd.length;
	const delimRow = _.find(delimTable, {begin: bd});

	if (!isValidDelim(delimRow, "begin", ps.text, bdPos)) {
		// This delimiter is escaped. Pass this character and keep searching.
		return findNextDelim(ps.at(bdPos + 1), delimTable);
	}

	const ed = delimRow.end;
	if (ed) {
		// This delimiter should be paired with an ending one.
		const edLength = ed.length;
		let edPos = ps.text.indexOf(ed, bdPos + bdLength);
		// Keep searching until we find a delimiter that is not escaped.
		while (edPos >= 0 && !isValidDelim(delimRow, "end", ps.text, edPos)) {
			edPos = ps.text.indexOf(ed, edPos + 1);
		}

		if (edPos < 0) {
			// Unmatched beginning delimiter; flag as error.
			ps.pushError(
				"Delimiter " + bd + " did not have matching end delimiter " + ed,
				bdPos
			);
			return {
				block: makeDelimBlock(bd),
				start: bdPos,
				end: bdPos + bdLength,
			};
		} else {
			return {
				block: makeDelimBlock(ps.text.slice(bdPos, edPos + edLength), delimRow),
				start: bdPos,
				end: edPos + edLength,
			};
		}
	} else {
		return {
			block: makeDelimBlock(bd, delimRow),
			start: bdPos,
			end: bdPos + bdLength,
		};
	}
};

// ps is a parser state.
// delimTable behaves like in findNextDelim.
// This returns an array of blocks with the same format as the findNextDelim
// return. All text remaining in the parser will be searched for delimiters.
// The parser state will be at the same position and have recorded errors.
const splitByDelimSet = function(ps, delimTable) {
	ps = PS.convert(ps);
	if (!delimTable) {
		return [makeDelimBlock(ps.text.slice(ps.pos))];
	}

	let currPs = ps;
	const blocks = [];
	let delimResult;

	while (!currPs.done()) {
		delimResult = findNextDelim(currPs, delimTable);
		if (delimResult) {
			if (currPs.pos < delimResult.start) {
				blocks.push(
					makeDelimBlock(currPs.text.slice(currPs.pos, delimResult.start))
				);
			}
			blocks.push(delimResult.block);
			currPs = currPs.at(delimResult.end);
		} else {
			blocks.push(makeDelimBlock(currPs.text.slice(currPs.pos)));
			break;
		}
	}

	return blocks;
};

module.exports = {
	makeDelimBlock,
	findNextDelim,
	splitByDelimSet,
};
